import { environment } from '../../../environments/environment';

export type EmailSignatureLayoutId =
  | 'classic'
  | 'accent'
  | 'horizontal'
  | 'minimal'
  | 'photo'
  | 'photo-accent'
  | 'photo-compact'
  | 'photo-logo'
  | 'text-image'
  | 'blank';

export type SocialNetworkId =
  | 'linkedin'
  | 'facebook'
  | 'x'
  | 'instagram'
  | 'youtube'
  | 'whatsapp'
  | 'tiktok'
  | 'messenger';

export interface EmailSignatureFieldValues {
  fullName: string;
  jobTitle: string;
  companyName: string;
  email: string;
  phone: string;
  website: string;
  accentColor: string;
  logoUrl: string;
  /** URL de la foto de perfil del firmante. */
  photoUrl: string;
  /** Texto libre HTML arriba de la firma (disclaimer, datos, etc.). */
  introHtml: string;
  /** Texto libre HTML del layout texto + imagen. */
  bodyHtml: string;
  /** Imagen subida al bucket público (layout texto + imagen). */
  signImageUrl: string;
  /** Ancho en px de la imagen; el alto se calcula proporcional (height:auto). */
  signImageWidth: number;
  socialLinkedin: string;
  socialFacebook: string;
  socialX: string;
  socialInstagram: string;
  socialYoutube: string;
  socialWhatsapp: string;
  socialTiktok: string;
  socialMessenger: string;
}

export interface SocialNetworkDef {
  id: SocialNetworkId;
  label: string;
  /** Clave en EmailSignatureFieldValues */
  field: keyof EmailSignatureFieldValues;
  placeholder: string;
  /** Color de marca (referencia UI) */
  color: string;
  /** Nombre de archivo en common/images/social-icons del bucket público */
  iconFile: string;
  /** URL pública del icono (preview en la app) */
  iconUrl: string;
}

export interface EmailSignaturePreset {
  id: EmailSignatureLayoutId;
  label: string;
  description: string;
  icon: string;
  /** Si es true, el usuario parte de un editor vacío. */
  blank?: boolean;
  /** Layouts que muestran foto de perfil. */
  usesPhoto?: boolean;
  /** Layouts que muestran logo de empresa. */
  usesLogo?: boolean;
  /** Layout de texto libre + imagen cargada. */
  usesTextImage?: boolean;
}

export interface CompanyEmailSignature {
  id: string;
  name: string;
  layoutId: EmailSignatureLayoutId;
  /** Plantilla HTML en formato Handlebars (vars del usuario remitente). */
  html: string;
  fields: EmailSignatureFieldValues;
  /** True si el usuario pasó a edición libre (editor/HTML) y ya no se regenera desde campos. */
  customHtml: boolean;
  updatedAt: number;
}

/** Modo de generación: preview con valores de muestra, o plantilla Handlebars. */
export type SignatureBuildMode = 'preview' | 'handlebars';

/** Variables Handlebars rellenadas con el usuario que envía el correo. */
export const SIGNATURE_USER_VARS = {
  fullName: 'userFullName',
  jobTitle: 'userJobTitle',
  phone: 'userPhone',
  email: 'userEmail',
  photoUrl: 'userPhotoUrl',
  initials: 'userInitials'
} as const;

const SOCIAL_ICONS_BASE = 'common/images/social-icons';

const SOCIAL_ICON_NAMES = [
  'envelope-fill-18',
  'phone-fill-18',
  'globe-pointer-fill-18',
  'linkedin',
  'facebook',
  'x-twitter',
  'instagram',
  'youtube',
  'whatsapp',
  'tik-tok',
  'messenger'
] as const;

/**
 * URL absoluta en el bucket público (misma para preview web y HTML de correo).
 */
export function socialIconPublicUrl(fileName: string): string {
  const base = (environment.publicStorageBaseUrl || '').replace(/\/$/, '');
  const normalized = fileName.replace(/\.png$/i, '.svg');
  return `${base}/${SOCIAL_ICONS_BASE}/${normalized}`;
}

/**
 * Normaliza cualquier URL de icono de firma al bucket público del environment actual.
 */
export function rewriteSignatureIconUrls(html: string): string {
  if (!html) {
    return html;
  }
  let out = html;
  for (const name of SOCIAL_ICON_NAMES) {
    const target = socialIconPublicUrl(`${name}.svg`);
    const pattern = new RegExp(
      String.raw`(?:https?:\/\/[^"'>\s]+\/|(?:\.\.?\/)?assets\/images\/icons\/nucleoapp\/)${name}\.(?:svg|png)`,
      'gi'
    );
    out = out.replace(pattern, target);
  }
  return out;
}

const CONTACT_ICON_FILES = {
  email: 'envelope-fill-18.svg',
  phone: 'phone-fill-18.svg',
  website: 'globe-pointer-fill-18.svg'
} as const;

export const SOCIAL_NETWORKS: SocialNetworkDef[] = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    field: 'socialLinkedin',
    placeholder: 'https://linkedin.com/in/usuario',
    color: '#0A66C2',
    iconFile: 'linkedin.svg',
    iconUrl: ''
  },
  {
    id: 'facebook',
    label: 'Facebook',
    field: 'socialFacebook',
    placeholder: 'https://facebook.com/pagina',
    color: '#1877F2',
    iconFile: 'facebook.svg',
    iconUrl: ''
  },
  {
    id: 'x',
    label: 'X (Twitter)',
    field: 'socialX',
    placeholder: 'https://x.com/usuario',
    color: '#000000',
    iconFile: 'x-twitter.svg',
    iconUrl: ''
  },
  {
    id: 'instagram',
    label: 'Instagram',
    field: 'socialInstagram',
    placeholder: 'https://instagram.com/usuario',
    color: '#E4405F',
    iconFile: 'instagram.svg',
    iconUrl: ''
  },
  {
    id: 'youtube',
    label: 'YouTube',
    field: 'socialYoutube',
    placeholder: 'https://youtube.com/@canal',
    color: '#FF0000',
    iconFile: 'youtube.svg',
    iconUrl: ''
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    field: 'socialWhatsapp',
    placeholder: 'https://wa.me/5215550000000',
    color: '#25D366',
    iconFile: 'whatsapp.svg',
    iconUrl: ''
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    field: 'socialTiktok',
    placeholder: 'https://tiktok.com/@usuario',
    color: '#000000',
    iconFile: 'tik-tok.svg',
    iconUrl: ''
  },
  {
    id: 'messenger',
    label: 'Messenger',
    field: 'socialMessenger',
    placeholder: 'https://m.me/usuario',
    color: '#0084FF',
    iconFile: 'messenger.svg',
    iconUrl: ''
  }
];

// URLs públicas para preview en UI (mismo origen que el correo).
for (const network of SOCIAL_NETWORKS) {
  network.iconUrl = socialIconPublicUrl(network.iconFile);
}

export const EMAIL_SIGNATURE_PRESETS: EmailSignaturePreset[] = [
  {
    id: 'classic',
    label: 'Clásica',
    description: 'Nombre, cargo y datos de contacto en bloque vertical.',
    icon: 'pi pi-align-left'
  },
  {
    id: 'accent',
    label: 'Con acento',
    description: 'Barra de color a la izquierda y tipografía limpia.',
    icon: 'pi pi-bookmark'
  },
  {
    id: 'horizontal',
    label: 'Con logo',
    description: 'Logo a la izquierda y datos a la derecha.',
    icon: 'pi pi-image',
    usesLogo: true
  },
  {
    id: 'photo',
    label: 'Con foto',
    description: 'Foto de perfil circular y datos a la derecha.',
    icon: 'pi pi-user',
    usesPhoto: true
  },
  {
    id: 'photo-accent',
    label: 'Foto + acento',
    description: 'Foto de perfil con barra de color y tipografía limpia.',
    icon: 'pi pi-id-card',
    usesPhoto: true
  },
  {
    id: 'photo-compact',
    label: 'Foto compacta',
    description: 'Foto pequeña junto al nombre, ideal para firmas cortas.',
    icon: 'pi pi-circle',
    usesPhoto: true
  },
  {
    id: 'photo-logo',
    label: 'Foto + logo',
    description: 'Foto de perfil a la izquierda, datos al centro y logo de empresa a la derecha.',
    icon: 'pi pi-images',
    usesPhoto: true,
    usesLogo: true
  },
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'Una sola línea compacta, ideal para respuestas rápidas.',
    icon: 'pi pi-minus'
  },
  {
    id: 'text-image',
    label: 'Texto + imagen',
    description: 'Texto libre con editor y una imagen subida al bucket público.',
    icon: 'pi pi-file-edit',
    usesTextImage: true
  },
  {
    id: 'blank',
    label: 'Desde cero',
    description: 'Editor vacío: diseña con p-editor o HTML libre.',
    icon: 'pi pi-pencil',
    blank: true
  }
];

export function layoutUsesPhoto(layoutId: EmailSignatureLayoutId | null | undefined): boolean {
  if (!layoutId) {
    return false;
  }
  return EMAIL_SIGNATURE_PRESETS.some((p) => p.id === layoutId && p.usesPhoto);
}

export function layoutUsesLogo(layoutId: EmailSignatureLayoutId | null | undefined): boolean {
  if (!layoutId) {
    return false;
  }
  return EMAIL_SIGNATURE_PRESETS.some((p) => p.id === layoutId && p.usesLogo);
}

export function layoutUsesTextImage(layoutId: EmailSignatureLayoutId | null | undefined): boolean {
  if (!layoutId) {
    return false;
  }
  return EMAIL_SIGNATURE_PRESETS.some((p) => p.id === layoutId && p.usesTextImage);
}

export const SIGN_IMAGE_WIDTH_DEFAULT = 180;
export const SIGN_IMAGE_WIDTH_MIN = 40;
export const SIGN_IMAGE_WIDTH_MAX = 600;

export function clampSignImageWidth(value: number | string | null | undefined): number {
  const n = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value);
  if (!Number.isFinite(n)) {
    return SIGN_IMAGE_WIDTH_DEFAULT;
  }
  return Math.min(SIGN_IMAGE_WIDTH_MAX, Math.max(SIGN_IMAGE_WIDTH_MIN, Math.round(n)));
}

export function defaultSignatureFields(partial?: Partial<EmailSignatureFieldValues>): EmailSignatureFieldValues {
  return {
    fullName: partial?.fullName ?? 'Nombre Apellido',
    jobTitle: partial?.jobTitle ?? 'Cargo',
    companyName: partial?.companyName ?? 'Empresa',
    email: partial?.email ?? 'correo@empresa.com',
    phone: partial?.phone ?? '(555) 000-0000',
    website: partial?.website ?? 'www.empresa.com',
    accentColor: partial?.accentColor ?? '#0ea5e9',
    logoUrl: partial?.logoUrl ?? '',
    photoUrl: partial?.photoUrl ?? '',
    introHtml: partial?.introHtml ?? '',
    bodyHtml: partial?.bodyHtml ?? '<p>Escribe aquí el texto de tu firma.</p>',
    signImageUrl: partial?.signImageUrl ?? '',
    signImageWidth: clampSignImageWidth(partial?.signImageWidth),
    socialLinkedin: partial?.socialLinkedin ?? '',
    socialFacebook: partial?.socialFacebook ?? '',
    socialX: partial?.socialX ?? '',
    socialInstagram: partial?.socialInstagram ?? '',
    socialYoutube: partial?.socialYoutube ?? '',
    socialWhatsapp: partial?.socialWhatsapp ?? '',
    socialTiktok: partial?.socialTiktok ?? '',
    socialMessenger: partial?.socialMessenger ?? ''
  };
}

function esc(value: string): string {
  return (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function hb(varName: string): string {
  return `{{${varName}}}`;
}

function hbIf(varName: string, content: string, elseContent = ''): string {
  if (elseContent) {
    return `{{#if ${varName}}}${content}{{else}}${elseContent}{{/if}}`;
  }
  return `{{#if ${varName}}}${content}{{/if}}`;
}

function initialsFromName(fullName: string): string {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

/** Contexto de muestra para previsualizar una plantilla Handlebars. */
export function buildSignaturePreviewContext(
  fields: EmailSignatureFieldValues | Partial<EmailSignatureFieldValues>
): Record<string, string> {
  const fullName = fields.fullName ?? '';
  return {
    [SIGNATURE_USER_VARS.fullName]: fullName,
    [SIGNATURE_USER_VARS.jobTitle]: fields.jobTitle ?? '',
    [SIGNATURE_USER_VARS.phone]: fields.phone ?? '',
    [SIGNATURE_USER_VARS.email]: fields.email ?? '',
    [SIGNATURE_USER_VARS.photoUrl]: fields.photoUrl ?? '',
    [SIGNATURE_USER_VARS.initials]: initialsFromName(fullName)
  };
}

/**
 * Renderiza una plantilla Handlebars simple ({{var}} y {{#if var}}…{{else}}…{{/if}}).
 * Suficiente para previsualizar firmas en el cliente sin dependencia extra.
 */
export function renderSignatureHandlebars(
  template: string,
  context: Record<string, string>
): string {
  if (!template) {
    return '';
  }
  let out = template;
  for (let i = 0; i < 12; i++) {
    const next = out.replace(
      /\{\{#if\s+([A-Za-z][A-Za-z0-9_]*)\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g,
      (_m, key: string, ifBlock: string, elseBlock = '') => {
        const val = (context[key] ?? '').toString().trim();
        return val ? ifBlock : elseBlock;
      }
    );
    if (next === out) {
      break;
    }
    out = next;
  }
  return out.replace(/\{\{([A-Za-z][A-Za-z0-9_]*)\}\}/g, (_m, key: string) => esc(context[key] ?? ''));
}

function normalizeUrl(url: string): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('mailto:')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function linkOrText(href: string, label: string, color: string): string {
  const safeHref = esc(href);
  const safeLabel = esc(label);
  if (!href.trim()) {
    return safeLabel;
  }
  return `<a href="${safeHref}" style="color:${esc(color)};text-decoration:none;">${safeLabel}</a>`;
}

function activeSocialNetworks(fields: EmailSignatureFieldValues): { def: SocialNetworkDef; url: string }[] {
  return SOCIAL_NETWORKS
    .map((def) => {
      const raw = String(fields[def.field] ?? '').trim();
      return raw ? { def, url: normalizeUrl(raw) } : null;
    })
    .filter((item): item is { def: SocialNetworkDef; url: string } => item != null);
}

function contactIconImg(kind: keyof typeof CONTACT_ICON_FILES, alt: string, size = 14): string {
  return `<img src="${esc(socialIconPublicUrl(CONTACT_ICON_FILES[kind]))}" alt="${esc(alt)}" width="${size}" height="${size}" style="display:block;border:0;outline:none;width:${size}px;height:${size}px;" />`;
}

function contactLine(
  kind: keyof typeof CONTACT_ICON_FILES,
  contentHtml: string,
  visible: boolean,
  iconSize = 14
): string {
  if (!visible || !contentHtml.trim()) {
    return '';
  }
  return `<tr>
  <td style="padding:2px 8px 2px 0;vertical-align:middle;width:${iconSize + 2}px;">${contactIconImg(kind, kind, iconSize)}</td>
  <td style="padding:2px 0;vertical-align:middle;font-size:12px;line-height:1.4;color:#64748b;">${contentHtml}</td>
</tr>`;
}

function buildContactRowsHtml(
  phone: string,
  emailHtml: string,
  webHtml: string,
  rawPhone: string,
  rawEmail: string,
  rawWebsite: string,
  iconSize = 14,
  mode: SignatureBuildMode = 'preview'
): string {
  let rows: string;
  if (mode === 'handlebars') {
    const phoneRow = hbIf(
      SIGNATURE_USER_VARS.phone,
      `<tr>
  <td style="padding:2px 8px 2px 0;vertical-align:middle;width:${iconSize + 2}px;">${contactIconImg('phone', 'Teléfono', iconSize)}</td>
  <td style="padding:2px 0;vertical-align:middle;font-size:12px;line-height:1.4;color:#64748b;">${hb(SIGNATURE_USER_VARS.phone)}</td>
</tr>`
    );
    const emailRow = hbIf(
      SIGNATURE_USER_VARS.email,
      `<tr>
  <td style="padding:2px 8px 2px 0;vertical-align:middle;width:${iconSize + 2}px;">${contactIconImg('email', 'Email', iconSize)}</td>
  <td style="padding:2px 0;vertical-align:middle;font-size:12px;line-height:1.4;color:#64748b;"><a href="mailto:${hb(SIGNATURE_USER_VARS.email)}" style="color:inherit;text-decoration:none;">${hb(SIGNATURE_USER_VARS.email)}</a></td>
</tr>`
    );
    const webRow = contactLine('website', webHtml, !!rawWebsite.trim(), iconSize);
    rows = [phoneRow, emailRow, webRow].filter(Boolean).join('');
  } else {
    rows = [
      contactLine('phone', phone, !!rawPhone.trim(), iconSize),
      contactLine('email', emailHtml, !!rawEmail.trim(), iconSize),
      contactLine('website', webHtml, !!rawWebsite.trim(), iconSize)
    ]
      .filter(Boolean)
      .join('');
  }

  if (!rows) {
    return '';
  }

  return `
<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin-top:10px;border-collapse:collapse;">
  ${rows}
</table>`.trim();
}

/** Fila de iconos de redes sociales (tabla HTML compatible con clientes de correo). */
export function buildSocialIconsHtml(fields: EmailSignatureFieldValues, iconSize = 22): string {
  const networks = activeSocialNetworks(fields);
  if (networks.length === 0) {
    return '';
  }

  const cells = networks
    .map(({ def, url }) => {
      return `<td style="padding:0 6px 0 0;vertical-align:middle;">
  <a href="${esc(url)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;border:0;outline:none;">
    <img src="${esc(socialIconPublicUrl(def.iconFile))}" alt="${esc(def.label)}" width="${iconSize}" height="${iconSize}" style="display:block;border:0;outline:none;width:${iconSize}px;height:${iconSize}px;" />
  </a>
</td>`;
    })
    .join('');

  return `
<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin-top:10px;border-collapse:collapse;">
  <tr>
    ${cells}
  </tr>
</table>`.trim();
}

function isEmptyRichHtml(html: string): boolean {
  const cleaned = (html ?? '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
  return cleaned.length === 0;
}

/** Antepone el bloque de texto libre (disclaimer / datos) encima del layout. */
function prependIntroHtml(introHtml: string, bodyHtml: string): string {
  if (isEmptyRichHtml(introHtml) || !bodyHtml.trim()) {
    return bodyHtml;
  }

  return `
<table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="font-family:Arial,Helvetica,sans-serif;border-collapse:collapse;">
  <tr>
    <td style="padding:0 0 12px 0;border-bottom:1px solid #e2e8f0;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:1.55;color:#64748b;">
      ${introHtml}
    </td>
  </tr>
  <tr>
    <td style="padding:12px 0 0 0;">
      ${bodyHtml}
    </td>
  </tr>
</table>`.trim();
}

function buildLogoCellHtml(
  fields: EmailSignatureFieldValues,
  size = 64,
  align: 'left' | 'right' = 'right'
): string {
  const company = esc(fields.companyName || 'Logo');
  const padding = align === 'right' ? 'padding-left:16px;' : 'padding-right:16px;';
  if (fields.logoUrl?.trim()) {
    return `<td style="${padding}vertical-align:middle;width:${size}px;">
  <img src="${esc(fields.logoUrl.trim())}" alt="${company}" width="${size}" height="${size}" style="display:block;border-radius:8px;object-fit:contain;border:0;outline:none;width:${size}px;height:${size}px;" />
</td>`;
  }

  const color = esc(fields.accentColor || '#0ea5e9');
  const initial = esc((fields.companyName || 'P').charAt(0).toUpperCase());
  const fontSize = Math.max(14, Math.round(size * 0.34));
  return `<td style="${padding}vertical-align:middle;width:${size}px;">
  <div style="width:${size}px;height:${size}px;border-radius:8px;background:${color};color:#fff;font-family:Arial,Helvetica,sans-serif;font-size:${fontSize}px;font-weight:700;text-align:center;line-height:${size}px;">
    ${initial}
  </div>
</td>`;
}

function buildPhotoCellHtml(
  fields: EmailSignatureFieldValues,
  size: number,
  borderRadius = '50%',
  mode: SignatureBuildMode = 'preview'
): string {
  const color = esc(fields.accentColor || '#0ea5e9');
  const fontSize = Math.max(12, Math.round(size * 0.34));

  if (mode === 'handlebars') {
    const img = `<img src="${hb(SIGNATURE_USER_VARS.photoUrl)}" alt="${hb(SIGNATURE_USER_VARS.fullName)}" width="${size}" height="${size}" style="display:block;border-radius:${borderRadius};object-fit:cover;border:0;outline:none;width:${size}px;height:${size}px;" />`;
    const fallback = `<div style="width:${size}px;height:${size}px;border-radius:${borderRadius};background:${color};color:#fff;font-family:Arial,Helvetica,sans-serif;font-size:${fontSize}px;font-weight:700;text-align:center;line-height:${size}px;">${hb(SIGNATURE_USER_VARS.initials)}</div>`;
    return `<td style="padding-right:16px;vertical-align:middle;width:${size}px;">
  ${hbIf(SIGNATURE_USER_VARS.photoUrl, img, fallback)}
</td>`;
  }

  const alt = esc(fields.fullName || 'Foto de perfil');
  if (fields.photoUrl?.trim()) {
    return `<td style="padding-right:16px;vertical-align:middle;width:${size}px;">
  <img src="${esc(fields.photoUrl.trim())}" alt="${alt}" width="${size}" height="${size}" style="display:block;border-radius:${borderRadius};object-fit:cover;border:0;outline:none;width:${size}px;height:${size}px;" />
</td>`;
  }

  const initials = esc(initialsFromName(fields.fullName));
  return `<td style="padding-right:16px;vertical-align:middle;width:${size}px;">
  <div style="width:${size}px;height:${size}px;border-radius:${borderRadius};background:${color};color:#fff;font-family:Arial,Helvetica,sans-serif;font-size:${fontSize}px;font-weight:700;text-align:center;line-height:${size}px;">
    ${initials}
  </div>
</td>`;
}

export function buildSignatureHtml(
  layoutId: EmailSignatureLayoutId,
  fields: EmailSignatureFieldValues,
  mode: SignatureBuildMode = 'preview'
): string {
  const f = defaultSignatureFields(fields);
  const color = f.accentColor || '#0ea5e9';
  const hbMode = mode === 'handlebars';
  const name = hbMode ? hb(SIGNATURE_USER_VARS.fullName) : esc(f.fullName);
  const title = hbMode ? hb(SIGNATURE_USER_VARS.jobTitle) : esc(f.jobTitle);
  const company = esc(f.companyName);
  const phone = hbMode ? hb(SIGNATURE_USER_VARS.phone) : esc(f.phone);
  const emailHtml = hbMode
    ? `<a href="mailto:${hb(SIGNATURE_USER_VARS.email)}" style="color:${esc(color)};text-decoration:none;">${hb(SIGNATURE_USER_VARS.email)}</a>`
    : linkOrText(f.email ? `mailto:${f.email}` : '', f.email, color);
  const webHref = f.website ? normalizeUrl(f.website) : '';
  const webHtml = linkOrText(webHref, f.website, color);
  const contactHtml = buildContactRowsHtml(
    phone,
    emailHtml,
    webHtml,
    hbMode ? '1' : f.phone,
    hbMode ? '1' : f.email,
    f.website,
    14,
    mode
  );
  const socialHtml = buildSocialIconsHtml(f);
  const socialCompact = buildSocialIconsHtml(f, 16);
  const titleWithCompany = hbMode
    ? company
      ? hbIf(SIGNATURE_USER_VARS.jobTitle, `${title} · ${company}`, company)
      : hbIf(SIGNATURE_USER_VARS.jobTitle, title)
    : `${title}${company ? ` · ${company}` : ''}`;

  let bodyHtml = '';

  switch (layoutId) {
    case 'blank':
      return '';
    case 'text-image': {
      const text = isEmptyRichHtml(f.bodyHtml)
        ? '<p style="margin:0;color:#94a3b8;">Escribe el texto de la firma…</p>'
        : f.bodyHtml;
      const width = clampSignImageWidth(f.signImageWidth);
      const imageBlock = f.signImageUrl?.trim()
        ? `<img class="sign-image" src="${esc(f.signImageUrl.trim())}" alt="Imagen de firma" width="${width}" style="display:block;border:0;outline:none;width:${width}px;max-width:100%;height:auto;border-radius:8px;" />`
        : `<div style="width:${width}px;max-width:100%;height:${Math.round(width * 0.66)}px;border-radius:8px;border:1px dashed #cbd5e1;background:#f8fafc;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;font-size:12px;text-align:center;line-height:${Math.round(width * 0.66)}px;">
    Sin imagen
  </div>`;
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.55;color:#334155;">
  <tr>
    <td style="vertical-align:top;padding:0 0 12px 0;font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.55;color:#334155;">
      ${text}
    </td>
  </tr>
  <tr>
    <td style="vertical-align:top;padding:0;">
      ${imageBlock}
    </td>
  </tr>
</table>`.trim();
      return bodyHtml;
    }
    case 'photo': {
      const photoCell = buildPhotoCellHtml(f, 72, '50%', mode);
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#475569;">
  <tr>
    ${photoCell}
    <td style="vertical-align:middle;">
      <div style="font-size:16px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="color:#64748b;margin-top:2px;">${titleWithCompany}</div>
      ${contactHtml}
      ${socialHtml}
    </td>
  </tr>
</table>`.trim();
      break;
    }
    case 'photo-accent': {
      const photoCell = buildPhotoCellHtml(f, 68, '50%', mode);
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#475569;">
  <tr>
    ${photoCell}
    <td style="width:4px;background:${esc(color)};border-radius:2px;"></td>
    <td style="padding-left:14px;vertical-align:middle;">
      <div style="font-size:16px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="color:${esc(color)};font-weight:600;margin-top:2px;">${hbMode ? hbIf(SIGNATURE_USER_VARS.jobTitle, title) : title}</div>
      <div style="margin-top:2px;color:#64748b;">${company}</div>
      ${contactHtml}
      ${socialHtml}
    </td>
  </tr>
</table>`.trim();
      break;
    }
    case 'photo-compact': {
      const photoCell = buildPhotoCellHtml(f, 40, '50%', mode);
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.45;color:#334155;">
  <tr>
    ${photoCell.replace('padding-right:16px;', 'padding-right:10px;')}
    <td style="vertical-align:middle;">
      <div style="font-size:14px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="font-size:12px;color:#64748b;">${titleWithCompany}</div>
      ${contactHtml.replace('margin-top:10px;', 'margin-top:6px;')}
      ${socialCompact}
    </td>
  </tr>
</table>`.trim();
      break;
    }
    case 'photo-logo': {
      const photoCell = buildPhotoCellHtml(f, 72, '50%', mode);
      const logoCell = buildLogoCellHtml(f, 64, 'right');
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#475569;">
  <tr>
    ${photoCell}
    <td style="vertical-align:middle;padding-right:12px;">
      <div style="font-size:16px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="color:#64748b;margin-top:2px;">${titleWithCompany}</div>
      ${contactHtml}
      ${socialHtml}
    </td>
    <td style="width:1px;background:#e2e8f0;"></td>
    ${logoCell}
  </tr>
</table>`.trim();
      break;
    }
    case 'minimal': {
      const minimalBits = hbMode
        ? [
            hbIf(
              SIGNATURE_USER_VARS.phone,
              `${contactIconImg('phone', 'Teléfono', 12)}&nbsp;${hb(SIGNATURE_USER_VARS.phone)}`
            ),
            hbIf(
              SIGNATURE_USER_VARS.email,
              `${contactIconImg('email', 'Email', 12)}&nbsp;${emailHtml}`
            )
          ].filter(Boolean)
        : [
            f.phone.trim() ? `${contactIconImg('phone', 'Teléfono', 12)}&nbsp;${phone}` : '',
            f.email.trim() ? `${contactIconImg('email', 'Email', 12)}&nbsp;${emailHtml}` : ''
          ].filter(Boolean);
      const titlePart = hbMode
        ? hbIf(
            SIGNATURE_USER_VARS.jobTitle,
            `<span style="color:#94a3b8;"> · </span><span>${title}</span>`
          )
        : title
          ? `<span style="color:#94a3b8;"> · </span><span>${title}</span>`
          : '';
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.45;color:#334155;">
  <tr>
    <td>
      <strong style="color:#0f172a;">${name}</strong>
      ${titlePart}
      ${minimalBits.length ? `<div style="margin-top:6px;font-size:12px;color:#64748b;">${minimalBits.join('<span style="color:#94a3b8;"> · </span>')}</div>` : ''}
      ${socialCompact ? `<div style="margin-top:6px;">${socialCompact}</div>` : ''}
    </td>
  </tr>
</table>`.trim();
      break;
    }
    case 'horizontal': {
      const logoCell = f.logoUrl
        ? `<td style="padding-right:16px;vertical-align:middle;">
            <img src="${esc(f.logoUrl)}" alt="${company}" width="72" height="72" style="display:block;border-radius:8px;object-fit:contain;" />
          </td>`
        : `<td style="padding-right:16px;vertical-align:middle;">
            <div style="width:72px;height:72px;border-radius:8px;background:${esc(color)};color:#fff;font-family:Arial,Helvetica,sans-serif;font-size:22px;font-weight:700;text-align:center;line-height:72px;">
              ${esc((f.companyName || 'P').charAt(0).toUpperCase())}
            </div>
          </td>`;
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#475569;">
  <tr>
    ${logoCell}
    <td style="vertical-align:middle;border-left:2px solid ${esc(color)};padding-left:16px;">
      <div style="font-size:16px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="color:#64748b;margin-top:2px;">${titleWithCompany}</div>
      ${contactHtml}
      ${socialHtml}
    </td>
  </tr>
</table>`.trim();
      break;
    }
    case 'accent':
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#475569;">
  <tr>
    <td style="width:4px;background:${esc(color)};border-radius:2px;"></td>
    <td style="padding-left:14px;">
      <div style="font-size:16px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="color:${esc(color)};font-weight:600;margin-top:2px;">${hbMode ? hbIf(SIGNATURE_USER_VARS.jobTitle, title) : title}</div>
      <div style="margin-top:2px;color:#64748b;">${company}</div>
      ${contactHtml}
      ${socialHtml}
    </td>
  </tr>
</table>`.trim();
      break;
    case 'classic':
    default:
      bodyHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#475569;">
  <tr>
    <td>
      <div style="font-size:16px;font-weight:700;color:#0f172a;">${name}</div>
      <div style="color:#64748b;">${hbMode ? hbIf(SIGNATURE_USER_VARS.jobTitle, title) : title}</div>
      <div style="font-weight:600;color:#0f172a;margin-top:2px;">${company}</div>
      <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:2px;">
        ${contactHtml.replace('margin-top:10px;', 'margin-top:8px;')}
      </div>
      ${socialHtml}
    </td>
  </tr>
</table>`.trim();
      break;
  }

  return prependIntroHtml(f.introHtml, bodyHtml);
}
